import { useParams } from "react-router-dom";
import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { FileText, GitCompare, Handshake, TrendingUp, ShieldCheck, Building2, Users, Cctv, Mail, Phone, MapPin, Wallet, GraduationCap, BadgeCheck, Target, Rocket, CheckCircle2, Quote, MessageCircle, Search } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Seo from "@/components/Seo";

function Hero({ eyebrow, title, sub }) {
  return (
    <section className="bg-navy-900 text-white">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <span className="brand-label text-brand">{eyebrow}</span>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter max-w-3xl">{title}</h1>
        {sub && <p className="mt-4 text-navy-300 max-w-2xl text-lg">{sub}</p>}
      </div>
    </section>
  );
}

export function HowItWorks() {
  const steps = [
    [FileText, "Post your requirement", "Share location, number of guards, shifts, contract duration and budget. It takes under 2 minutes."],
    [GitCompare, "Compare verified quotations", "Multiple PSARA & GST verified agencies respond. Compare price, rating, compliance and SLA side-by-side."],
    [Handshake, "Hire & sign digitally", "Accept the best quote and generate a digital contract with clear replacement and payment terms."],
    [TrendingUp, "Manage from one dashboard", "Track attendance, request replacements, report incidents, pay invoices and rate performance."],
  ];
  return (
    <div>
      <Seo title="How It Works — Hire Security Guards in 4 Steps | GuardDekho.com" description="Learn how GuardDekho.com works: post your requirement, compare verified quotations, hire and sign digitally, then manage guards, attendance and payments from one dashboard." />
      <Hero eyebrow="How It Works" title="Find. Compare. Hire. Manage." sub="A transparent marketplace built specifically for private security services in India." />
      <section className="max-w-5xl mx-auto px-6 py-16 space-y-10">
        {steps.map(([Icon, t, d], i) => (
          <div key={t} className="flex gap-6 items-start">
            <div className="shrink-0 w-14 h-14 rounded-2xl bg-brand text-white flex items-center justify-center font-heading text-xl font-bold">{i + 1}</div>
            <div className="flex-1 border-b border-navy-200 pb-10">
              <div className="flex items-center gap-2"><Icon size={20} className="text-brand" /><h3 className="font-heading text-xl font-bold text-navy-900">{t}</h3></div>
              <p className="mt-2 text-navy-600 leading-relaxed">{d}</p>
            </div>
          </div>
        ))}
        <div className="text-center pt-4">
          <Link to="/post-requirement"><Button className="bg-navy-900 hover:bg-navy-800 h-12 px-8">Post a Requirement</Button></Link>
        </div>
      </section>
    </div>
  );
}

export function ForAgencies() {
  const benefits = [
    [Users, "Qualified leads", "Receive security requirements from real customers in your service areas."],
    [TrendingUp, "Grow revenue", "Win contracts, manage guards and get paid faster with digital invoicing."],
    [ShieldCheck, "Build trust", "Get verified once and display PSARA, GST and rating badges to win more business."],
    [Cctv, "Sell technology", "Offer CCTV, access control and biometric services through the marketplace."],
  ];
  return (
    <div>
      <Seo title="For Security Agencies — Get Leads & Win Contracts | GuardDekho.com" description="Join GuardDekho.com, India's verified security marketplace. Get qualified leads, submit quotations, manage guards and grow your security agency's revenue." />
      <Hero eyebrow="For Agencies" title="Win more security contracts on GuardDekho" sub="Join India's verified security marketplace. Get leads, submit quotations and manage your entire operation." />
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-5">
          {benefits.map(([Icon, t, d]) => (
            <div key={t} className="card-lift bg-white border border-navy-200 rounded-xl p-6 flex gap-4">
              <div className="w-11 h-11 rounded-lg bg-navy-900 text-white flex items-center justify-center shrink-0"><Icon size={20} /></div>
              <div><h3 className="font-heading font-semibold text-lg text-navy-900">{t}</h3><p className="mt-1 text-navy-600 text-sm">{d}</p></div>
            </div>
          ))}
        </div>

        <div className="mt-16">
          <span className="brand-label text-brand">Total Solution</span>
          <h2 className="mt-2 font-heading text-2xl sm:text-3xl font-bold text-navy-900 tracking-tight">Everything to start, certify & scale your agency</h2>
          <p className="mt-2 text-navy-600 max-w-2xl">From incorporation to execution — GuardDekho is the complete platform for security agencies.</p>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[[Building2, "Incorporation to Execution", "End-to-end support to set up, register and run your security agency."],
              [Wallet, "Finance Solutions for All Employees", "Payroll, salary advances and financial products for your entire workforce."],
              [BadgeCheck, "PSARA Certification", "Get PSARA licensed with expert compliance and documentation assistance."],
              [GraduationCap, "Guard Training Certification", "Certified guard training courses to upskill and qualify your staff."]].map(([Icon, t, d]) => (
              <div key={t} className="card-lift bg-navy-50 border border-navy-200 rounded-xl p-6" data-testid={`fa-solution-${t.replace(/\s/g, "-").toLowerCase()}`}>
                <div className="w-11 h-11 rounded-lg bg-brand text-white flex items-center justify-center"><Icon size={20} /></div>
                <h3 className="mt-4 font-heading font-semibold text-navy-900">{t}</h3>
                <p className="mt-2 text-sm text-navy-600 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 rounded-2xl bg-brand p-10 text-center">
          <h2 className="font-heading text-3xl font-bold text-white">Register your agency today</h2>
          <p className="mt-2 text-white/90">Sign in with Google, create your company profile and start receiving leads.</p>
          <Link to="/agency/register"><Button className="mt-6 bg-white text-brand hover:bg-navy-900 hover:text-white h-12 px-8 font-semibold" data-testid="agency-register-cta">Register as Agency</Button></Link>
        </div>
      </section>
    </div>
  );
}

export function ForBusinesses() {
  const types = ["Housing Societies", "Hotels", "Hospitals", "Factories", "Warehouses", "Offices", "Schools", "Retail Stores", "Construction", "Events", "Individuals"];
  return (
    <div>
      <Seo title="For Businesses — Hire Verified Security Agencies | GuardDekho.com" description="From housing societies to factories, hospitals and events — hire trained, verified security guards and agencies across India and manage everything digitally on GuardDekho.com." />
      <Hero eyebrow="For Businesses" title="Secure your premises with verified agencies" sub="From housing societies to factories — hire trained, verified security professionals and manage everything digitally." />
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex flex-wrap gap-3">
          {types.map((t) => <span key={t} className="px-4 py-2 rounded-full bg-white border border-navy-200 text-navy-700 font-medium">{t}</span>)}
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {[[Building2, "Transparent pricing", "Compare quotations from multiple agencies with no hidden costs."],
            [ShieldCheck, "Only verified agencies", "Every legally verified agency is checked by our team before the badge is shown."],
            [TrendingUp, "Full visibility", "Attendance reports, incident logs, invoices and performance in one place."]].map(([Icon, t, d]) => (
            <div key={t} className="bg-navy-50 border border-navy-200 rounded-xl p-6">
              <Icon size={22} className="text-brand" /><h3 className="mt-3 font-heading font-semibold text-lg text-navy-900">{t}</h3><p className="mt-1 text-navy-600 text-sm">{d}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-12"><Link to="/post-requirement"><Button className="bg-navy-900 hover:bg-navy-800 h-12 px-8">Request Quotations</Button></Link></div>
      </section>
    </div>
  );
}

export function About() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/content/about").then((r) => setC(r.data)).catch(() => setC({})); }, []);
  if (!c) return <div className="min-h-[60vh]"><Hero eyebrow="About GuardDekho" title="About GuardDekho.com" /></div>;
  const intro = c.intro || [];
  const founderBio = c.founder_bio || [];

  return (
    <div data-testid="about-page">
      <Seo title="About GuardDekho.com — India's Security Guard Marketplace" description="About GuardDekho.com, operated by Vent Cure Hospitality LLP — a security guard marketplace connecting customers with verified security agencies and guards across India." />
      <Hero eyebrow="About GuardDekho" title={c.heading || "GuardDekho.com"} sub={c.tagline} />

      <section className="max-w-5xl mx-auto px-6 py-14">
        <div className="grid md:grid-cols-2 gap-4">
          {intro.map((p, i) => (
            <div key={i} className="card-lift bg-white border border-navy-200 rounded-xl p-6 text-navy-700 leading-relaxed" data-testid={`about-intro-${i}`}>{p}</div>
          ))}
        </div>
      </section>

      <section className="bg-navy-50 border-y border-navy-200">
        <div className="max-w-5xl mx-auto px-6 py-14 grid md:grid-cols-2 gap-6">
          <div className="bg-white border border-navy-200 rounded-2xl p-8" data-testid="about-vision">
            <div className="w-12 h-12 rounded-xl bg-brand text-white flex items-center justify-center"><Target size={22} /></div>
            <h2 className="mt-4 font-heading text-2xl font-bold text-navy-900">Our Vision</h2>
            <p className="mt-3 text-navy-600 leading-relaxed">{c.vision}</p>
          </div>
          <div className="bg-white border border-navy-200 rounded-2xl p-8" data-testid="about-mission">
            <div className="w-12 h-12 rounded-xl bg-navy-900 text-white flex items-center justify-center"><Rocket size={22} /></div>
            <h2 className="mt-4 font-heading text-2xl font-bold text-navy-900">Our Mission</h2>
            <ul className="mt-4 space-y-2.5">
              {(c.mission || []).map((m, i) => (
                <li key={i} className="flex gap-2.5 text-navy-700"><CheckCircle2 size={18} className="text-brand shrink-0 mt-0.5" /><span>{m}</span></li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-16">
        <span className="brand-label text-brand">Leadership</span>
        <h2 className="mt-2 font-heading text-2xl sm:text-3xl font-bold text-navy-900">Founder</h2>
        <div className="mt-6 grid md:grid-cols-3 gap-6">
          <div className="bg-navy-900 text-white rounded-2xl p-8 flex flex-col items-start" data-testid="about-founder">
            <div className="w-16 h-16 rounded-2xl bg-brand flex items-center justify-center font-heading text-2xl font-extrabold">
              {(c.founder_name || "K").split(" ").map((w) => w[0]).slice(0, 2).join("")}
            </div>
            <h3 className="mt-4 font-heading text-xl font-bold">{c.founder_name}</h3>
            <p className="text-brand text-sm font-medium">{c.founder_title}</p>
            <p className="mt-4 text-navy-300 text-sm leading-relaxed">{c.founder_experience}</p>
          </div>
          <div className="md:col-span-2 space-y-4">
            {founderBio.map((p, i) => <p key={i} className="text-navy-700 leading-relaxed">{p}</p>)}
            {c.founder_vision_statement && (
              <blockquote className="mt-4 border-l-4 border-brand bg-brand-50 rounded-r-xl p-5 text-navy-800 italic" data-testid="about-founder-quote">
                <Quote size={18} className="text-brand mb-2" />“{c.founder_vision_statement}”
              </blockquote>
            )}
          </div>
        </div>
      </section>

      <section className="bg-navy-50 border-y border-navy-200">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-center">
            <span className="brand-label text-brand">Why GuardDekho?</span>
            <h2 className="mt-2 font-heading text-2xl sm:text-3xl font-bold text-navy-900">Built for customers and agencies</h2>
          </div>
          <div className="mt-8 grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-navy-200 rounded-2xl p-8" data-testid="about-customer-benefits">
              <div className="flex items-center gap-2"><Users className="text-brand" size={20} /><h3 className="font-heading text-lg font-bold text-navy-900">For Customers</h3></div>
              <ul className="mt-4 space-y-2.5">
                {(c.customer_benefits || []).map((b, i) => (
                  <li key={i} className="flex gap-2.5 text-navy-700"><CheckCircle2 size={18} className="text-emerald-500 shrink-0 mt-0.5" /><span>{b}</span></li>
                ))}
              </ul>
            </div>
            <div className="bg-white border border-navy-200 rounded-2xl p-8" data-testid="about-agency-benefits">
              <div className="flex items-center gap-2"><Building2 className="text-brand" size={20} /><h3 className="font-heading text-lg font-bold text-navy-900">For Security Agencies</h3></div>
              <ul className="mt-4 space-y-2.5">
                {(c.agency_benefits || []).map((b, i) => (
                  <li key={i} className="flex gap-2.5 text-navy-700"><CheckCircle2 size={18} className="text-emerald-500 shrink-0 mt-0.5" /><span>{b}</span></li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {c.verification_note && (
        <section className="max-w-4xl mx-auto px-6 py-10">
          <div className="flex gap-3 rounded-xl border border-navy-200 bg-white p-5 text-sm text-navy-600" data-testid="about-verification-note">
            <ShieldCheck className="text-brand shrink-0" size={20} />
            <p>{c.verification_note}</p>
          </div>
        </section>
      )}

      <section className="max-w-5xl mx-auto px-6 pb-16">
        <div className="rounded-2xl bg-brand p-10 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white">{c.cta_heading || "Ready to get started with GuardDekho?"}</h2>
          {c.cta_text && <p className="mt-2 text-white/90">{c.cta_text}</p>}
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Link to="/agencies"><Button className="bg-white text-brand hover:bg-navy-900 hover:text-white h-11 px-7 font-semibold" data-testid="about-cta-find">Find Security Agency</Button></Link>
            <Link to="/post-requirement"><Button className="bg-navy-900 text-white hover:bg-navy-800 h-11 px-7 font-semibold" data-testid="about-cta-post">Post Your Requirement</Button></Link>
            <Link to="/agency/register"><Button variant="outline" className="border-white text-white hover:bg-white hover:text-brand h-11 px-7 font-semibold bg-transparent" data-testid="about-cta-register">Register Your Agency</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export function Contact() {
  const [c, setC] = useState(null);
  const [form, setForm] = useState({ name: "", mobile: "", email: "", city: "", user_type: "Customer", requirement: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => { api.get("/content/contact").then((r) => setC(r.data)).catch(() => setC({})); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await api.post("/enquiries", form);
      setDone(true);
      toast.success(r.data.message);
      setForm({ name: "", mobile: "", email: "", city: "", user_type: "Customer", requirement: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit enquiry. Please try again.");
    } finally { setSubmitting(false); }
  };

  if (!c) return <div className="min-h-[60vh]"><Hero eyebrow="Contact" title="Contact GuardDekho" /></div>;

  return (
    <div data-testid="contact-page">
      <Seo title="Contact GuardDekho.com — Security Marketplace Support" description="Contact GuardDekho.com for help hiring security agencies or registering your agency. Reach our team by phone, email or WhatsApp, or submit an enquiry online." />
      <Hero eyebrow="Contact" title="Contact GuardDekho" sub={c.intro} />
      <section className="max-w-6xl mx-auto px-6 py-14 grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-navy-900 text-white rounded-2xl p-7">
            <p className="font-heading font-bold text-lg">{c.company_name || "GuardDekho.com"}</p>
            {c.operated_by && <p className="text-navy-300 text-sm mt-0.5">{c.operated_by}</p>}
            <div className="mt-6 space-y-4 text-sm">
              {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-3 hover:text-brand transition-colors" data-testid="contact-email-link"><span className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center"><Mail size={16} /></span>{c.email}</a>}
              {c.phone && <a href={`tel:${c.phone.replace(/\s/g, "")}`} className="flex items-center gap-3 hover:text-brand transition-colors" data-testid="contact-phone-link"><span className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center"><Phone size={16} /></span>{c.phone}</a>}
              {c.whatsapp && <a href={`https://wa.me/${c.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 hover:text-brand transition-colors" data-testid="contact-whatsapp-link"><span className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center"><MessageCircle size={16} /></span>WhatsApp</a>}
              {c.address && <div className="flex items-start gap-3"><span className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center shrink-0"><MapPin size={16} /></span><span className="text-navy-200 leading-relaxed">{c.address}</span></div>}
            </div>
          </div>
          <div className="grid gap-2.5">
            <Link to="/agencies"><Button className="w-full bg-brand hover:bg-brand-600 h-11" data-testid="contact-cta-find">Find Security Agency</Button></Link>
            <Link to="/post-requirement"><Button variant="outline" className="w-full border-navy-300 h-11" data-testid="contact-cta-post">Post Your Requirement</Button></Link>
            <Link to="/agency/register"><Button variant="outline" className="w-full border-navy-300 h-11" data-testid="contact-cta-register">Register Your Agency</Button></Link>
          </div>
        </div>

        <div className="lg:col-span-3">
          {c.form_enabled === false ? (
            <div className="bg-white border border-navy-200 rounded-2xl p-8 text-navy-600">The enquiry form is currently unavailable. Please reach us via email or phone.</div>
          ) : done ? (
            <div className="bg-white border border-emerald-200 rounded-2xl p-10 text-center" data-testid="contact-success">
              <CheckCircle2 className="mx-auto text-emerald-500" size={40} />
              <h3 className="mt-4 font-heading text-xl font-bold text-navy-900">Enquiry submitted</h3>
              <p className="mt-2 text-navy-600">Thank you. Your enquiry has been submitted successfully. Our team will contact you shortly.</p>
              <Button className="mt-6 bg-navy-900 hover:bg-navy-800" onClick={() => setDone(false)} data-testid="contact-another">Submit another enquiry</Button>
            </div>
          ) : (
            <form className="bg-white border border-navy-200 rounded-2xl p-6 sm:p-8 space-y-4" onSubmit={submit} data-testid="contact-form">
              <h3 className="font-heading text-lg font-bold text-navy-900">Submit an Enquiry</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-xs">Full Name *</Label><Input className="mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="enquiry-name" /></div>
                <div><Label className="text-xs">Mobile Number *</Label><Input className="mt-1" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} required data-testid="enquiry-mobile" /></div>
                <div><Label className="text-xs">Email Address</Label><Input type="email" className="mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="enquiry-email" /></div>
                <div><Label className="text-xs">City / Location</Label><Input className="mt-1" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="enquiry-city" /></div>
              </div>
              <div>
                <Label className="text-xs">User Type</Label>
                <Select value={form.user_type} onValueChange={(v) => setForm({ ...form, user_type: v })}>
                  <SelectTrigger className="mt-1" data-testid="enquiry-usertype"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Customer", "Security Agency", "Business", "Other"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Requirement</Label><Input className="mt-1" placeholder="e.g. 5 guards for a hotel in Mumbai" value={form.requirement} onChange={(e) => setForm({ ...form, requirement: e.target.value })} data-testid="enquiry-requirement" /></div>
              <div><Label className="text-xs">Message</Label><Textarea rows={4} className="mt-1" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="enquiry-message" /></div>
              <Button type="submit" disabled={submitting} className="bg-brand hover:bg-brand-600 w-full h-11" data-testid="enquiry-submit">{submitting ? "Submitting…" : "Submit Enquiry"}</Button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

export function FAQ() {
  const [faqs, setFaqs] = useState([]);
  const [query, setQuery] = useState("");
  useEffect(() => { api.get("/faqs").then((r) => setFaqs(r.data)).catch(() => setFaqs([])); }, []);

  const q = query.trim().toLowerCase();
  const match = (f) => !q || f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q);
  const groups = [
    ["customer", "Customer FAQs", Users],
    ["agency", "Security Agency FAQs", Building2],
  ];

  return (
    <div data-testid="faq-page">
      <Seo
        title="FAQ — Security Guard Marketplace Help | GuardDekho.com"
        description="Frequently asked questions about GuardDekho.com for customers and security agencies — hiring, verification, quotations, pricing and account help."
        jsonLd={faqs.length ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.slice(0, 20).map((f) => ({
            "@type": "Question",
            name: f.question,
            acceptedAnswer: { "@type": "Answer", text: f.answer },
          })),
        } : undefined}
      />
      <Hero eyebrow="FAQ" title="Frequently asked questions" sub="Answers for customers and security agencies using GuardDekho.com." />
      <section className="max-w-3xl mx-auto px-6 py-14">
        <div className="relative mb-8">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-navy-400" size={18} />
          <Input className="pl-11 h-12" placeholder="Search FAQs…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="faq-search" />
        </div>
        {groups.map(([cat, title, Icon]) => {
          const items = faqs.filter((f) => f.category === cat && match(f));
          if (!items.length) return null;
          return (
            <div key={cat} className="mb-10" data-testid={`faq-group-${cat}`}>
              <div className="flex items-center gap-2 mb-4"><Icon size={20} className="text-brand" /><h2 className="font-heading text-xl font-bold text-navy-900">{title}</h2></div>
              <Accordion type="single" collapsible className="space-y-3">
                {items.map((f) => (
                  <AccordionItem key={f.id} value={f.id} className="bg-white border border-navy-200 rounded-xl px-5" data-testid={`faq-item-${f.id}`}>
                    <AccordionTrigger className="font-heading font-semibold text-navy-900 hover:no-underline text-left">{f.question}</AccordionTrigger>
                    <AccordionContent className="text-navy-600 leading-relaxed">{f.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          );
        })}
        {faqs.filter(match).length === 0 && (
          <div className="text-center py-12 text-navy-500" data-testid="faq-empty">No FAQs match “{query}”.</div>
        )}
      </section>
    </div>
  );
}

const LEGAL = {
  terms: ["Terms & Conditions", "GuardDekho operates as a technology marketplace that facilitates connections and contracts between customers and independent security service providers. GuardDekho is not the employer of any security guard and does not directly provide security services. Regulatory responsibility, including compliance with the PSARA Act and applicable labour laws, remains with the relevant service provider and customer as applicable. By using the platform you agree to use it lawfully and provide accurate information."],
  privacy: ["Privacy Policy", "We collect only the information necessary to operate the marketplace. Sensitive KYC and compliance documents are stored securely and are not publicly accessible. We do not expose personal phone numbers until the platform's business rules allow it. You may request access to or deletion of your data subject to legal retention requirements."],
  disclaimer: ["Marketplace Disclaimer", "Listings, ratings and quotations are provided by third-party agencies. While GuardDekho verifies compliance documents before granting a Verified badge, customers are responsible for their own due diligence before entering into a contract. Numerical claims shown on the platform reflect actual platform data."],
  verification: ["Agency Verification Policy", "Verification status is controlled exclusively by the GuardDekho platform administration. An agency is never marked legally verified merely because documents have been uploaded. Documents may include PSARA licence, GST certificate, company registration, KYC, insurance and other applicable regulatory records. Verification may be set to Pending, Under Review, Verified, Rejected or Expired, with expiry alerts."],
  refund: ["Refund Policy", "Refunds for platform fees and prepaid services are handled case-by-case in line with the specific service terms. Contract-level payments between customers and agencies are governed by the executed contract's payment and cancellation terms."],
  cancellation: ["Cancellation Policy", "Contracts may be cancelled subject to the notice period specified in the digital contract (typically 30 days). Cancellation of subscriptions takes effect at the end of the current billing cycle."],
  data: ["Data Protection Policy", "We implement role-based access control, encrypted storage of sensitive data, audit logging and secure document handling. Backups are maintained to protect against data loss. Access to KYC documents is restricted to authorized platform administrators."],
};

export function Legal() {
  const { doc } = useParams();
  const [title, body] = LEGAL[doc] || ["Legal", "Document not found."];
  return (
    <div>
      <Hero eyebrow="Legal" title={title} />
      <section className="max-w-3xl mx-auto px-6 py-16 text-navy-700 leading-relaxed">
        <p>{body}</p>
        <div className="mt-10 flex flex-wrap gap-3">
          {Object.entries(LEGAL).map(([k, v]) => (
            <Link key={k} to={`/legal/${k}`} className={`text-sm px-3 py-1.5 rounded-full border ${k === doc ? "bg-navy-900 text-white border-navy-900" : "border-navy-200 text-navy-600 hover:border-brand"}`}>{v[0]}</Link>
          ))}
        </div>
      </section>
    </div>
  );
}
